import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  Filter,
  Search,
  Tag,
  X,
  Zap,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { MasterRibbon } from '../../../components/MasterRibbon';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';
import { useMinhaMesaRibbon } from '../hooks/useMinhaMesaRibbon';
import type { QueueSortMode } from '../hooks/useMinhaMesaRibbon';
import type { WorkFilters, PeriodFilter, SortMode } from '../hooks/useWorkFilters';
import type { CardDensity } from './WorkKanbanView';
import type { WorkItemSource } from '../types/workItem';
import type { TaskState } from '../engine/taskStateMachine';
import type { SupportPanelTab } from '../hooks/useUnifiedPanelBadges';

export type WorkViewMode = 'list' | 'kanban' | 'timeline';

type DropdownName = 'todos' | 'filtros' | 'fifo' | null;

const dropdownPanelClass =
  'ribbon-dropdown-panel enterprise-font fixed z-[999999] rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] p-1 text-[var(--text-primary)] shadow-lg';
const dropdownButtonClass =
  'flex h-8 w-full items-center gap-2 rounded-sm border border-transparent px-2 text-left text-[11px] font-medium text-[var(--text-primary)] hover:bg-[var(--button-hover)]';
const ribbonChipClass =
  'flex h-[30px] items-center gap-1 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] px-2.5 text-[11px] font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)] cursor-pointer';

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'this_year', label: 'Este ano' },
];

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: 'fila', label: 'Fila' },
  { value: 'fifo', label: 'FIFO' },
  { value: 'sla_asc', label: 'SLA' },
  { value: 'lead_desc', label: 'Lead Time' },
  { value: 'idle_desc', label: 'Tempo parado' },
  { value: 'priority', label: 'Prioridade' },
];

const TYPE_OPTIONS: { value: WorkItemSource | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'action', label: 'Ação' },
  { value: 'problem', label: 'Problema' },
  { value: 'workflow', label: 'Workflow' },
];

const QUEUE_SORT_OPTIONS: { value: QueueSortMode; label: string; icon: React.ReactNode }[] = [
  { value: 'score', label: 'Auto', icon: <Zap size={12} /> },
  { value: 'risk', label: 'Risco SLA', icon: <AlertTriangle size={12} /> },
  { value: 'tp', label: 'Maior TP', icon: <Clock size={12} /> },
];

export interface MinhaMesaRibbonProps {
  searchValue: string;
  workViewMode: WorkViewMode;
  filters: WorkFilters;
  hasActiveFilters: boolean;
  density: CardDensity;
  focusMode: boolean;
  cockpitActive: boolean;
  taskState?: TaskState;
  canComplete?: boolean;
  onFiltersChange: (updater: WorkFilters | ((prev: WorkFilters) => WorkFilters)) => void;
  onResetFilters: () => void;
  onDensityChange: () => void;
  onFocusModeToggle: () => void;
  onSearchChange: (value: string) => void;
  onSetWorkViewMode: (mode: WorkViewMode) => void;
  onClaimTask?: () => void;
  onStartTask?: () => void;
  onCompleteTask?: () => void;
  onPauseTask?: () => void;
  onResumeTask?: () => void;
  onRejectTask?: () => void;
  onTransferTask?: () => void;
  onEscalateTask?: () => void;
  onRegisterProblem?: () => void;
  onToggleHistory?: () => void;
  historyActive?: boolean;
  isCompleting?: boolean;
  isPausing?: boolean;
  isResuming?: boolean;
  isClaiming?: boolean;
  isStarting?: boolean;
  onPullNextTask?: () => void;
  canPullNext?: boolean;
  onExportProductivity?: () => void;
  queueSortMode?: QueueSortMode;
  onQueueSortModeChange?: (mode: QueueSortMode) => void;
  supportTab?: SupportPanelTab;
  supportPanelOpen?: boolean;
  onSupportTabChange?: (tab: SupportPanelTab) => void;
  supportSopDisabled?: boolean;
  supportHistoricoDisabled?: boolean;
  onOpenRequest?: () => void;
  requestCatalogOpen?: boolean;
}

function MinhaMesaRibbonImpl({
  searchValue,
  workViewMode,
  filters,
  hasActiveFilters,
  cockpitActive,
  onFiltersChange,
  onResetFilters,
  onSearchChange,
  onSetWorkViewMode,
  onToggleHistory,
  historyActive,
  onPullNextTask,
  canPullNext = false,
  onExportProductivity,
  queueSortMode = 'score',
  onQueueSortModeChange,
  supportTab,
  supportPanelOpen,
  onSupportTabChange,
  supportSopDisabled,
  supportHistoricoDisabled,
  onOpenRequest,
  requestCatalogOpen = false,
}: MinhaMesaRibbonProps) {
  const [openDropdown, setOpenDropdown] = useState<DropdownName>(null);
  const todosRef = useRef<HTMLDivElement>(null);
  const filtrosRef = useRef<HTMLDivElement>(null);
  const fifoRef = useRef<HTMLDivElement>(null);
  const [todosPos, setTodosPos] = useState({ top: 0, left: 0 });
  const [filtrosPos, setFiltrosPos] = useState({ top: 0, left: 0 });
  const [fifoPos, setFifoPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const update = () => {
      if (openDropdown === 'todos' && todosRef.current) {
        const r = todosRef.current.getBoundingClientRect();
        setTodosPos({ top: r.bottom + 4, left: r.left });
      }
      if (openDropdown === 'filtros' && filtrosRef.current) {
        const r = filtrosRef.current.getBoundingClientRect();
        setFiltrosPos({ top: r.bottom + 4, left: r.left });
      }
      if (openDropdown === 'fifo' && fifoRef.current) {
        const r = fifoRef.current.getBoundingClientRect();
        setFifoPos({ top: r.bottom + 4, left: r.left });
      }
    };
    const id = window.setTimeout(update, 0);
    if (openDropdown) {
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
    }
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-mm-dropdown="true"]')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  const currentTypeValue: WorkItemSource | 'all' =
    filters.sourceTypes.length === 1 ? filters.sourceTypes[0] : 'all';

  const periodActive = filters.period !== 'all';
  const filtrosActive =
    hasActiveFilters
    || currentTypeValue !== 'all'
    || Boolean(searchValue.trim())
    || queueSortMode !== 'score';

  // ── Seletores rápidos (afterGroups — alinhados à direita) ─────
  const filtersWidget = (
    <div className="flex shrink-0 flex-nowrap items-center gap-1.5 md:flex-nowrap" data-mm-dropdown="true">
      {/* Todos — período */}
      <div ref={todosRef}>
        <button
          type="button"
          data-mm-dropdown="true"
          onClick={() => setOpenDropdown(openDropdown === 'todos' ? null : 'todos')}
          className={`${ribbonChipClass} ${periodActive ? 'border-blue-300/60 text-blue-700 dark:border-blue-800/50 dark:text-blue-400' : ''}`}
          title="Período"
        >
          Todos
          <ChevronDown size={10} className="opacity-60" />
        </button>
      </div>
      {openDropdown === 'todos' && createPortal(
        <div data-mm-dropdown="true" className={dropdownPanelClass} style={{ top: todosPos.top, left: todosPos.left, minWidth: 148 }}>
          {PERIOD_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={dropdownButtonClass}
              onClick={() => {
                onFiltersChange((prev) => ({ ...prev, period: o.value, customFrom: null, customTo: null }));
                setOpenDropdown(null);
              }}
            >
              <Check size={12} className={filters.period === o.value ? 'opacity-100' : 'opacity-0'} />
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}

      {/* Filtros — tipo, busca, inteligência da fila */}
      <div ref={filtrosRef}>
        <button
          type="button"
          data-mm-dropdown="true"
          onClick={() => setOpenDropdown(openDropdown === 'filtros' ? null : 'filtros')}
          className={`${ribbonChipClass} ${filtrosActive ? 'border-amber-300/60 text-amber-700 dark:border-amber-800/50 dark:text-amber-400' : ''}`}
          title="Filtros avançados"
        >
          <Filter size={11} className="opacity-70" />
          Filtros
          <ChevronDown size={10} className="opacity-60" />
        </button>
      </div>
      {openDropdown === 'filtros' && createPortal(
        <div
          data-mm-dropdown="true"
          className={dropdownPanelClass}
          style={{ top: filtrosPos.top, left: Math.max(8, filtrosPos.left - 120), minWidth: 260 }}
        >
          {!cockpitActive && (
            <div className="border-b border-[var(--border-subtle)] px-2 py-2">
              <div className="mb-1 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">
                <Search size={10} /> Busca
              </div>
              <div className="flex items-center gap-1.5 rounded-sm border border-[var(--border-default)] bg-[var(--bg-canvas)] px-2">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Código, etapa ou descrição..."
                  className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0 py-1.5`}
                />
                {searchValue && (
                  <button type="button" onClick={() => onSearchChange('')} className="shrink-0 p-0.5 hover:bg-[var(--button-hover)] rounded-sm">
                    <X size={10} />
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="px-1 py-1">
            <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">Tipo</div>
            {TYPE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={dropdownButtonClass}
                onClick={() => {
                  onFiltersChange((prev) => ({ ...prev, sourceTypes: o.value === 'all' ? [] : [o.value as WorkItemSource] }));
                }}
              >
                <Tag size={12} className="opacity-50" />
                <Check size={12} className={currentTypeValue === o.value ? 'opacity-100' : 'opacity-0'} />
                {o.label}
              </button>
            ))}
          </div>
          <div className="my-1 h-px bg-[var(--border-subtle)]" />
          <div className="px-1 py-1">
            <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">Prioridade da fila</div>
            {QUEUE_SORT_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={dropdownButtonClass}
                onClick={() => onQueueSortModeChange?.(o.value)}
              >
                {o.icon}
                <Check size={12} className={queueSortMode === o.value ? 'opacity-100' : 'opacity-0'} />
                {o.label}
              </button>
            ))}
          </div>
          {filtrosActive && (
            <>
              <div className="my-1 h-px bg-[var(--border-subtle)]" />
              <button
                type="button"
                className="flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left text-[11px] font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                onClick={() => {
                  onResetFilters();
                  onSearchChange('');
                  onQueueSortModeChange?.('score');
                  setOpenDropdown(null);
                }}
              >
                <X size={12} />
                Limpar filtros
              </button>
            </>
          )}
        </div>,
        document.body,
      )}

      {/* FIFO — ordenação */}
      <div ref={fifoRef}>
        <button
          type="button"
          data-mm-dropdown="true"
          onClick={() => setOpenDropdown(openDropdown === 'fifo' ? null : 'fifo')}
          className={`${ribbonChipClass} ${filters.sort !== 'fila' ? 'border-blue-300/60 text-blue-700 dark:border-blue-800/50 dark:text-blue-400' : ''}`}
          title="Ordenação da fila: risco, SLA e tempo parado"
        >
          <ArrowUpDown size={11} className="opacity-70" />
          {SORT_OPTIONS.find((option) => option.value === filters.sort)?.label ?? 'Fila'}
          <ChevronDown size={10} className="opacity-60" />
        </button>
      </div>
      {openDropdown === 'fifo' && createPortal(
        <div data-mm-dropdown="true" className={dropdownPanelClass} style={{ top: fifoPos.top, left: fifoPos.left, minWidth: 148 }}>
          {SORT_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={dropdownButtonClass}
              onClick={() => {
                onFiltersChange((prev) => ({ ...prev, sort: o.value }));
                setOpenDropdown(null);
              }}
            >
              <Check size={12} className={filters.sort === o.value ? 'opacity-100' : 'opacity-0'} />
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );

  const ribbonConfig = useMinhaMesaRibbon({
    canPullNext,
    cockpitActive,
    onPullNextTask,
    onOpenLauncher: onOpenRequest,
    requestCatalogOpen,
    workViewMode,
    onSetWorkViewMode,
    historyActive,
    onToggleHistory,
    onExportProductivity,
    queueSortMode,
    onQueueSortModeChange,
    filtersWidget,
    supportTab,
    supportPanelOpen,
    onSupportTabChange,
    supportSopDisabled,
    supportHistoricoDisabled,
  });

  return <MasterRibbon config={ribbonConfig} />;
}

export const MinhaMesaRibbon = React.memo(MinhaMesaRibbonImpl);
